import { useMutation } from "@tanstack/react-query";
import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import toast from "react-hot-toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  resetProfessionalTotp,
  type ProfessionalTotpState,
} from "@/services/professionalTotpService";

interface ProfessionalTotpCardProps {
  userId: string;
  totp: ProfessionalTotpState;
  /**
   * Refetch whatever carried this block — the caller owns its own query keys.
   * Returns `unknown` so a react-query `refetch` can be passed straight in
   * without the caller wrapping it to discard its result.
   */
  onResetComplete: () => void | Promise<unknown>;
}

/**
 * A professional's second factor, and the last-resort reset.
 *
 * ONE COMPONENT FOR BOTH DETAIL PAGES (spec b8731a82, control C-4). The notary
 * and accountant screens are 400-line parallel clones that this codebase keeps
 * in lockstep BY HAND — and the text being shared here is the copy stating what
 * destroying someone's second factor does to them. That is the last string in
 * the app that should be free to drift between two screens.
 *
 * THE CARD IS NOT DECORATION AROUND THE BUTTON. Three different situations hide
 * behind "I can't get in": never enrolled (nothing to reset, and the endpoint
 * would delete nothing while reporting success), enrolled with codes left (the
 * answer is a recovery code, and a reset invalidates the codes that would have
 * worked), enrolled with codes spent (the only case the reset exists for).
 * Without this state on screen all three look identical.
 */
export function ProfessionalTotpCard({
  userId,
  totp,
  onResetComplete,
}: ProfessionalTotpCardProps): JSX.Element {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const resetMutation = useMutation({
    mutationFn: () => resetProfessionalTotp(userId),
    onSuccess: async () => {
      toast.success(t("professionalTotp.resetSuccess"));
      setConfirmOpen(false);
      await onResetComplete();
    },
    onError: () => toast.error(t("professionalTotp.resetError")),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-foreground">
          {t("professionalTotp.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">
          <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("professionalTotp.fieldStatus")}
            </dt>
            <dd className="flex items-center gap-2">
              {totp.enrolled ? (
                <>
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <StatusBadge variant="success">{t("professionalTotp.statusEnrolled")}</StatusBadge>
                </>
              ) : (
                <>
                  <ShieldOff className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <StatusBadge variant="warning">{t("professionalTotp.statusNotEnrolled")}</StatusBadge>
                </>
              )}
            </dd>
          </div>
          {totp.enrolled ? (
            <>
              <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t("professionalTotp.fieldConfirmedAt")}
                </dt>
                <dd className="text-sm text-foreground">
                  {totp.confirmedAt != null ? formatDisplayDate(totp.confirmedAt) : "—"}
                </dd>
              </div>
              <div className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-2">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t("professionalTotp.fieldRecoveryCodes")}
                </dt>
                <dd className="text-sm text-foreground">
                  {t("professionalTotp.recoveryCodesRemaining", {
                    count: totp.recoveryCodesRemaining,
                  })}
                  {/* The steer that keeps a working factor alive: with codes
                      left, a reset is the worse answer (FM-4). */}
                  {totp.recoveryCodesRemaining > 0 ? (
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t("professionalTotp.recoveryCodesHint")}
                    </span>
                  ) : null}
                </dd>
              </div>
            </>
          ) : null}
        </dl>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          {/* The reason is rendered, not just the disabled state: an admin who
              presses a dead button learns nothing, and the endpoint would
              return the same 204 as a real reset (FM-2). */}
          <p className="text-xs text-muted-foreground">
            {totp.enrolled
              ? t("professionalTotp.resetAvailableHint")
              : t("professionalTotp.resetUnavailableReason")}
          </p>
          <Button
            type="button"
            variant="destructive"
            disabled={!totp.enrolled || resetMutation.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {resetMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("professionalTotp.resetSubmitting")}
              </>
            ) : (
              t("professionalTotp.resetAction")
            )}
          </Button>
        </div>
      </CardContent>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("professionalTotp.confirmTitle")}
        // All three consequences, stated. An admin agreeing to "reset 2FA"
        // without being told the sessions die and the codes go with it has not
        // been told what they are agreeing to.
        description={t("professionalTotp.confirmDescription", {
          count: totp.recoveryCodesRemaining,
        })}
        confirmText={t("professionalTotp.resetAction")}
        cancelText={t("professionalTotp.confirmCancel")}
        loadingText={t("professionalTotp.resetSubmitting")}
        variant="destructive"
        isLoading={resetMutation.isPending}
        onConfirm={() => resetMutation.mutate()}
      />
    </Card>
  );
}
